/**
 * Owner-side assessment reviews fetcher — the question-anchored analogue of
 * useReviews. Polls every 30s and refreshes on the assessment_review.created SSE.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listAssessmentReviewsForConversation,
  patchAssessmentReviewComment,
  deleteAssessmentReviewById,
} from '../../../services/assessmentReviewsAPI';
import { conversationUpdatesService } from '../../../services/conversationUpdatesService';
import type { AssessmentOwnerReview, AssessmentOwnerReviewComment, ReviewCommentStatus } from './types';

const POLL_INTERVAL_MS = 30_000;

interface UseAssessmentReviewsResult {
  reviews: AssessmentOwnerReview[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateCommentStatus: (
    reviewId: string,
    commentId: string,
    update: { status?: ReviewCommentStatus; creatorNote?: string | null; sentMessageId?: string | null },
  ) => Promise<AssessmentOwnerReviewComment | null>;
  removeReview: (reviewId: string) => Promise<void>;
}

export function useAssessmentReviews(conversationId: string | null, enabled: boolean): UseAssessmentReviewsResult {
  const [reviews, setReviews] = useState<AssessmentOwnerReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!conversationId || !enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      const res = await listAssessmentReviewsForConversation(conversationId);
      setReviews(res.reviews);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load reviews');
    } finally {
      inFlightRef.current = false;
    }
  }, [conversationId, enabled]);

  useEffect(() => {
    if (!conversationId || !enabled) return;
    setLoading(true);
    fetchOnce().finally(() => setLoading(false));
    const id = window.setInterval(() => { fetchOnce(); }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [conversationId, enabled, fetchOnce]);

  useEffect(() => {
    if (!conversationId || !enabled) return;
    const unsubscribe = conversationUpdatesService.onConversationUpdate((cid, updates) => {
      if (cid !== conversationId) return;
      if (updates?.updateType === 'assessment_review.created') fetchOnce();
    });
    return unsubscribe;
  }, [conversationId, enabled, fetchOnce]);

  const updateCommentStatus = useCallback<UseAssessmentReviewsResult['updateCommentStatus']>(
    async (reviewId, commentId, update) => {
      if (!conversationId) return null;
      setReviews((prev) =>
        prev.map((r) =>
          r.id !== reviewId
            ? r
            : {
                ...r,
                comments: r.comments.map((c) =>
                  c.id !== commentId
                    ? c
                    : {
                        ...c,
                        status: update.status ?? c.status,
                        creatorNote: update.creatorNote !== undefined ? update.creatorNote : c.creatorNote,
                        sentMessageId: update.sentMessageId !== undefined ? update.sentMessageId : c.sentMessageId,
                        sentAt: update.status === 'sent' ? new Date().toISOString() : update.status ? null : c.sentAt,
                      },
                ),
              },
        ),
      );
      try {
        const res = await patchAssessmentReviewComment(conversationId, reviewId, commentId, update);
        return res.comment;
      } catch (err: any) {
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to update comment');
        await fetchOnce();
        return null;
      }
    },
    [conversationId, fetchOnce],
  );

  const removeReview = useCallback<UseAssessmentReviewsResult['removeReview']>(
    async (reviewId) => {
      if (!conversationId) return;
      const snapshot = reviews;
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      try {
        await deleteAssessmentReviewById(conversationId, reviewId);
      } catch (err: any) {
        setError(err?.response?.data?.message ?? err?.message ?? 'Failed to delete review');
        setReviews(snapshot);
      }
    },
    [conversationId, reviews],
  );

  return { reviews, loading, error, refresh: fetchOnce, updateCommentStatus, removeReview };
}
